import { Moment } from 'moment'

import React, { useEffect } from 'react'

import styled from 'styled-components'

import { Panel } from 'components/Panel'

import ActiveAlerts from 'pages/Device/AlertsPanel/ActiveAlerts'
import AlertsHistory from 'pages/Device/AlertsPanel/AlertsHistory'
import HomeownerPanelHeaderNotification from 'pages/shared/HomeownerAlert/HomeownerPanelHeaderNotification'
import LastUpdated from 'pages/Device/LastUpdated'

import useNexiaStream from 'hooks/useNexiaStream'
import { useWebsocketContext } from 'hooks/WebsocketContext'
import { useFeaturesContext } from 'hooks/FeaturesContext'

import { highestSeverity, updateAlertsSeverity } from 'helpers/alerts'

import { screenSize, NoData, screenWidthBreakpoints, alertsAndRthPanelLongVersionTitleWidth } from 'styles/shared'
import { color } from 'styles/colors'

import ExtendedDisconnect from 'pages/shared/Device/ExtendedDisconnect'

export type Props = {
  deviceId: string
  deviceTimeZone: string
  reportCache: object
  showDayRthCallback: (day: Moment) => void
}

const AlertsPanel = ({ deviceId, deviceTimeZone, reportCache, showDayRthCallback }: Props) => {
  const { eventData: report } = useNexiaStream(deviceId, 'alarms', reportCache)
  const { subscribe, unsubscribe, payload: alertsReport } = useWebsocketContext(deviceId, 'alerts')
  const { enabledFeatures } = useFeaturesContext()

  useEffect(() => {
    subscribe(deviceId, 'alerts')

    return () => {
      unsubscribe(deviceId, 'alerts')
    }
  }, [deviceId, subscribe, unsubscribe])

  const areWebsocketsEnabled = enabledFeatures.includes('montblanc_websockets')
  const activeReport = areWebsocketsEnabled ? alertsReport : report
  const severity = highestSeverity(updateAlertsSeverity(activeReport?.alerts || []))

  const alertsLength = (activeReport?.alerts || []).length

  if (activeReport?.extendedDisconnect) {
    return (
      <>
        <ExtendedDisconnect title="Alerts" type="normal" />
        <S.Panel pingdomId="alerts-container" id="alerts" scrollToSection>
          <AlertsHistory deviceId={deviceId} deviceTimeZone={deviceTimeZone} showDayRthCallback={showDayRthCallback} />
        </S.Panel>
      </>
    )
  }

  return (
    <S.Panel pingdomId="alerts-container" id="alerts" scrollToSection>
      <S.Header>
        <S.PanelTitle severity={severity}>Alerts {alertsLength > 0 ? `(${alertsLength})` : ''}</S.PanelTitle>
        <HomeownerPanelHeaderNotification />
        {areWebsocketsEnabled ? (
          <S.LastUpdatedArea>
            {activeReport && <LastUpdated date={activeReport?.lastUpdatedAt} />}
          </S.LastUpdatedArea>
        ) : (
          <S.LastUpdatedArea>{report && <LastUpdated date={report?.lastUpdatedAt} />}</S.LastUpdatedArea>
        )}
      </S.Header>
      {
        (!activeReport
          ? <S.NoData><h1>No Data Received Yet</h1></S.NoData>
          : (
            <>
              <ActiveAlerts alerts={activeReport.alerts || []} showDayRthCallback={showDayRthCallback} />
              <AlertsHistory deviceId={deviceId} deviceTimeZone={deviceTimeZone} showDayRthCallback={showDayRthCallback} />
            </>
          )
        )
      }
    </S.Panel>
  )
}

export default AlertsPanel

const S = {
  Panel: styled(Panel) <{ id: string }>`
    scroll-margin-top: 60px;
    @media ${screenSize.small} {
      overflow: unset;
    }
  `,
  Header: styled(Panel.Header)`
    padding-right: 0px;
    flex: 1;
  `,
  PanelTitle: styled(Panel.Title).withConfig({
    shouldForwardProp: (prop) => !['severity'].includes(prop)
  }) <{ severity: string | null }>`
    width: max-content;
    ${({ severity }) => severity ? `border-bottom:3px solid ${color(`${severity}Alert`)}` : 'none'}`,
  LastUpdatedArea: styled.div`
    @media (min-width: ${screenWidthBreakpoints.devicePanelHeaderShortVersionBreakpoint}px) {
      width: ${alertsAndRthPanelLongVersionTitleWidth}px;
    }
  `,
  NoData: styled(NoData)`
    h1 {
      font-size: 24px;
    }
  `
}
